import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthState } from '@revalize/auth/frontend'
import { isFeatureEnabled, features, type FeatureName } from '@shared/features'
import {
  BREADCRUMB_BUFFER_SIZE,
  CONSOLE_BUFFER_SIZE,
  REQUEST_ID_BUFFER_SIZE,
} from '@shared/support-bundles/contract'
import { useApi } from '@/hooks/useAppApi'
import { ConfigService } from '@/services/ConfigService'
import { isProduction } from '@/utils/env'
import { getBrowserInfo } from '@/features/about/pages/AboutPage'
import { logger } from '@/utils/logger'
import { SupportBundleHost } from './SupportBundleHost'
import { isSupportBundlesEnabled } from './enabled'
import { assembleBundle } from './controller'
import { buildMetadata, type AboutFields } from './capture/metadata'
import { collectDiagnostics } from './collect'
import { createBreadcrumbTrail } from './capture/breadcrumbs'
import { createConsoleCapture } from './capture/console-buffer'
import { createRequestIdTrail } from './capture/request-ids'
import { installFetchObserver } from './capture/fetch-observer'
import { dataUrlToBytes } from './capture/screenshot'
import { isModelOpenHere } from './capture/open-model'
import { captureViewport } from './capture/dom-capture'
import { foreignObjectCapturer } from './capture/foreign-object-capturer'
import { serializeModel } from './capture/model'
import { createRedactor } from './redact'
import { collectOidcSecrets } from './known-secrets'
import { gzipJson } from './gzip'
import { submitBundle, type SubmitResult } from './submit'

/**
 * The capture channels are module-level singletons: they have to be recording
 * long before anyone presses the shortcut, and a second set would either see an
 * empty buffer or double-install its listeners.
 */
const consoleCapture = createConsoleCapture({ capacity: CONSOLE_BUFFER_SIZE })
export const breadcrumbs = createBreadcrumbTrail({ capacity: BREADCRUMB_BUFFER_SIZE })
export const requestIds = createRequestIdTrail({ capacity: REQUEST_ID_BUFFER_SIZE })

let installedCaptures = false

function resolvedFlags(): Record<string, boolean> {
  const prod = isProduction()
  const out: Record<string, boolean> = {}
  for (const name of Object.keys(features) as FeatureName[]) {
    out[name] = ConfigService.getResolvedFeature(name) ?? isFeatureEnabled(name, prod)
  }
  return out
}

/**
 * Loads the editor store only when a bundle actually needs it, and only when
 * the URL agrees a model is open.
 *
 * The route check is not belt-and-braces. Nothing clears the store on
 * navigate-away — `clearModel()` exists for exactly that and has no caller
 * outside tests — so the store happily reports a model the user left minutes
 * ago. Trusting it alone attaches customer engineering IP to a bundle sent from
 * the dashboard, under consent text that says "your open model".
 */
async function readModelState(pathname: string): Promise<{
  system: unknown
  calculationResults: unknown
  schematicSnapshot: string | null
} | null> {
  try {
    const { useModelStore } = await import('@/features/import/store/model-store')
    const state = useModelStore.getState()
    if (!isModelOpenHere(pathname, state.modelId)) return null
    return {
      system: state.system,
      calculationResults: state.calculationResults,
      schematicSnapshot: state.schematicSnapshot,
    }
  } catch {
    // Off the editor route the chunk may never have loaded. That is a bundle
    // with no model, not a failure.
    return null
  }
}

/**
 * Wires the live capture channels, the open model and the submit path into the
 * report dialog.
 *
 * The model store is reached through a dynamic import rather than a top-level
 * one: it is a 5,000-line module inside the lazily loaded editor chunk, and
 * importing it from a component mounted on every route — /login included —
 * would pull the whole editor into the eager bundle to serve a feature that is
 * usually not used.
 */
export function SupportBundleContainer(): JSX.Element | null {
  const api = useApi()
  const { user } = api
  const location = useLocation()
  const authState = useAuthState({ baseUrl: ConfigService.getApiBaseUrl() })
  const screenshotRef = useRef<Uint8Array | undefined>(undefined)
  const [hasModel, setHasModel] = useState(false)

  const enabled = isSupportBundlesEnabled() && !!user?.id

  // Environment block for the metadata. Same query key and fetcher as the
  // About page, so the two share one cache entry rather than each fetching
  // their own copy.
  const { data: about } = useQuery<AboutFields>({
    queryKey: ['about'],
    queryFn: (): Promise<AboutFields> => api.get('/about') as Promise<AboutFields>,
    enabled,
    staleTime: 10 * 60 * 1000,
  })

  useEffect((): void => {
    if (!enabled || installedCaptures) return
    try {
      consoleCapture.install()
      installFetchObserver({ requestIds, breadcrumbs })
      installedCaptures = true
    } catch (error) {
      // A capture failure must never be the reason the app fails to start.
      logger.warn('Diagnostics capture failed to install', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }, [enabled])

  // Route changes are the cheapest useful breadcrumb: they turn "it hung" into
  // "it hung after they came from the import wizard".
  useEffect((): void => {
    if (!enabled) return
    breadcrumbs.record({ action: 'route', target: location.pathname })
  }, [enabled, location.pathname])

  /**
   * Snapshots the screen before the dialog renders, so the bundle shows the app
   * state the user meant to report rather than the dialog covering it.
   */
  const onCapture = useCallback(async (): Promise<void> => {
    const state = await readModelState(location.pathname)
    setHasModel(state?.system != null)

    // Capture the page as the user sees it — panels, dialogs, the error toast —
    // which is usually where the diagnosis is. The stored Konva snapshot is a
    // fallback rather than an equivalent: it renders the whole diagram at a
    // normalised size regardless of zoom or pan, so it shows the model but not
    // what the user was looking at.
    screenshotRef.current =
      (await captureViewport({
        capture: foreignObjectCapturer,
        target: (): HTMLElement | null => document.body,
      })) ?? dataUrlToBytes(state?.schematicSnapshot ?? null)
  }, [location.pathname])

  const onSend = useCallback(
    async (
      message: string,
      options: { includeScreenshot: boolean; includeModel: boolean },
    ): Promise<SubmitResult> => {
      const state = await readModelState(location.pathname)
      const model = serializeModel({
        system: (state?.system ?? null) as never,
        calculationResults: state?.calculationResults ?? null,
      })

      // Built per submission, never once at module load. A redactor constructed
      // before login captures null tokens forever and ships every bundle
      // unredacted — and a test asserting a *known* token is scrubbed would
      // still pass, which is what makes that failure invisible.
      let appToken: string | null
      try {
        appToken = (await authState.getAppToken()) ?? null
      } catch {
        appToken = null
      }
      // The app JWT plus the OIDC session tokens. The refresh token especially:
      // it is the browser's most durable credential and it is opaque rather
      // than a JWT, so nothing downstream could ever pattern-match it.
      const redact = createRedactor([appToken, ...collectOidcSecrets()])

      const browser = getBrowserInfo()
      const assembled = await assembleBundle(
        {
          message,
          platform: `${browser.browser}/${browser.os}`,
          includeScreenshot: options.includeScreenshot,
          includeModel: options.includeModel,
        },
        {
          collect: () =>
            collectDiagnostics({
              console: consoleCapture,
              breadcrumbs,
              requestIds,
              metadata: () =>
                buildMetadata({
                  about,
                  user: user ?? null,
                  browser,
                  route: location.pathname,
                  featureFlags: resolvedFlags(),
                }),
            }),
          getModel: () => model,
          peekScreenshot: () => screenshotRef.current,
          gzip: gzipJson,
          redact,
        },
      )

      return submitBundle({
        message: assembled.message,
        platform: assembled.platform,
        diagnostics: assembled.diagnostics,
        screenshot: assembled.screenshot,
        model: assembled.model,
        screenshotState: assembled.screenshotState,
        modelState: assembled.modelState,
        token: appToken,
      })
    },
    [about, user, location.pathname, authState],
  )

  return (
    <SupportBundleHost
      enabled={enabled}
      hasModel={hasModel}
      onCapture={onCapture}
      onSend={onSend}
    />
  )
}
